import React, { useState, useEffect, useRef } from "react";
import { type ColumnDef, flexRender, type Header } from "@tanstack/react-table";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  GripVertical,
} from "lucide-react";
import { Reorder, useDragControls } from "motion/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { useTable } from "./use-table.hook";
import { cn } from "@/utils/cn";

// ─── Filter Types ─────────────────────────────────────────────────────────────

export type FilterDef =
  | { type: "text"; key: string; label: string; placeholder?: string; defaultValue?: string }
  | {
    type: "chips";
    key: string;
    label: string;
    options: string[];
    defaultValue?: string[];
  }
  | {
    type: "radio";
    key: string;
    label: string;
    options: string[];
    defaultValue?: string;
  }
  | {
    type: "toggle";
    key: string;
    label: string;
    description?: string;
    defaultValue?: boolean;
  };

export type FilterValues = Record<string, string | string[]>;

// ─── Cell Presets ─────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "bg-orange-100 text-orange-700",
  "bg-blue-100 text-blue-700",
  "bg-indigo-100 text-indigo-700",
  "bg-slate-100 text-slate-700",
  "bg-purple-100 text-purple-700",
  "bg-emerald-100 text-emerald-700",
];

export interface AvatarCellProps {
  name: string;
  subtitle?: string;
  src?: string | null;
  index?: number;
  onClick?: () => void;
}
export const AvatarCell: React.FC<AvatarCellProps> = ({
  name,
  subtitle,
  src,
  index = 0,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const initials = (name || "?").substring(0, 2).toUpperCase();
  const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const showImage = src && !imgError;

  return (
    <div className="flex items-center gap-3">
      {showImage ? (
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm p-0.5">
          <img
            src={src}
            alt={name}
            className="w-full h-full object-contain rounded"
            onError={() => setImgError(true)}
          />
        </div>
      ) : (
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 border border-white dark:border-slate-800 shadow-sm ${color}`}
        >
          {initials}
        </div>
      )}
      <div className="flex flex-col">
        <span
          className={cn(
            "text-[14px] font-bold text-slate-900 dark:text-white",
            onClick && "cursor-pointer hover:text-[--primary]",
          )}
          onClick={onClick}
        >
          {name}
        </span>
        {subtitle && (
          <span className="text-xs text-slate-500 mt-0.5">{subtitle}</span>
        )}
      </div>
    </div>
  );
};

export interface StatusCellProps {
  status: string;
}
export const StatusCell: React.FC<StatusCellProps> = ({ status }) => {
  const isActive = status.toLowerCase() === "active";
  const isPending = status.toLowerCase() === "pending";
  const cls = isActive
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
    : isPending
      ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
      : "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700";
  const dotCls = isActive
    ? "bg-emerald-500"
    : isPending
      ? "bg-amber-500"
      : "bg-slate-400";
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1.5 w-max ${cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
      {status}
    </span>
  );
};

export interface BadgeCellProps {
  label: string;
  variant?: "default" | "purple" | "orange";
}
export const BadgeCell: React.FC<BadgeCellProps> = ({
  label,
  variant = "default",
}) => {
  const cls =
    variant === "purple"
      ? "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20"
      : variant === "orange"
        ? "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20"
        : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700";
  return (
    <span
      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cls}`}
    >
      {label}
    </span>
  );
};

export interface BadgeListCellProps {
  items: string[];
}
export const BadgeListCell: React.FC<BadgeListCellProps> = ({ items }) => (
  <div className="flex items-center gap-1.5 flex-wrap">
    {items.map((item) => (
      <BadgeCell key={item} label={item} />
    ))}
  </div>
);

export interface DateCellProps {
  value: string | Date | null | undefined;
}
export const DateCell: React.FC<DateCellProps> = ({ value }) => {
  if (!value) return <span className="text-[13px] text-slate-400">—</span>;
  const d = typeof value === "string" ? new Date(value) : value;
  const formatted = isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  return (
    <span className="text-[13px] text-slate-500 dark:text-slate-400">
      {formatted}
    </span>
  );
};

export interface MonoCellProps {
  value: unknown;
}
export const MonoCell: React.FC<MonoCellProps> = ({ value }) => (
  <div className="text-[11px] font-mono text-slate-500 bg-slate-50/50 dark:bg-slate-900/50 p-2 rounded max-w-xs overflow-x-auto border border-slate-100 dark:border-slate-800">
    {typeof value === "string" ? value : JSON.stringify(value)}
  </div>
);

export interface ActionCellProps {
  label?: string;
  onClick: () => void;
}
export const ActionCell: React.FC<ActionCellProps> = ({
  label = "Manage",
  onClick,
}) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="text-[14px] font-bold text-[#1e293b] dark:text-slate-300 hover:text-[--primary] dark:hover:text-[--primary] transition-colors"
  >
    {label}
  </button>
);

// ─── TableHeaderCell ─────────────────────────────────────────────────────────

export const TableHeaderCell = ({ header }: { header: Header<any, any> }) => (
  <TableHead
    key={header.id}
    colSpan={header.colSpan}
    className={
      header.column.columnDef.meta?.align === "right" ? "text-right" : ""
    }
  >
    {header.isPlaceholder ? null : (
      <div
        className={cn(
          header.column.getCanSort()
            ? "cursor-pointer select-none inline-flex items-center gap-1"
            : "",
        )}
        onClick={header.column.getToggleSortingHandler()}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        {{ asc: " ↑", desc: " ↓" }[header.column.getIsSorted() as string] ??
          null}
      </div>
    )}
  </TableHead>
);

// ─── ComposedTable ────────────────────────────────────────────────────────────

export type ComposedTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  /** External search string (global fuzzy filter) */
  searchQuery?: string;
  searchPlaceholder?: string;
  /** Quick status tab filter */
  statusOptions?: string[];
  /** Field on T to compare for status tabs. Can be a function. */
  statusField?: keyof T | ((row: T) => string);
  /** Declarative "More Filters" popover definitions */
  filters?: FilterDef[];
  /** Custom server-side / manual filter function applied after tanstack */
  filterFn?: (row: T, values: FilterValues) => boolean;
  /** Show + add button */
  onAdd?: () => void;
  /** Empty state when no data at all */
  emptyState?: React.ReactNode;
  /** Label used in pagination text, e.g. "tenants" */
  itemLabel?: string;
  pageSize?: number;
  onSearchChange?: (search: string) => void;
  onFilterChange?: (values: FilterValues) => void;
  onStatusChange?: (status: string) => void;
  /**
   * Enables drag-to-sort. Called with the rows of the current page in their new
   * order once a drag ends. Dragging is disabled while a column sort is active.
   */
  onReorder?: (reorderedPageRows: T[]) => void;
  /** Stable key for a row, required for drag-to-sort (defaults to `row.id`). */
  getRowKey?: (row: T) => string;
};

type SortableRowProps = {
  rowKey: string;
  onDragEnd: () => void;
  children: React.ReactNode;
};

const SortableRow: React.FC<SortableRowProps> = ({ rowKey, onDragEnd, children }) => {
  const controls = useDragControls();
  const [dragging, setDragging] = useState(false);
  return (
    <Reorder.Item
      as="tr"
      value={rowKey}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => setDragging(true)}
      onDragEnd={() => {
        setDragging(false);
        onDragEnd();
      }}
      transition={{ type: "spring", stiffness: 500, damping: 40 }}
      whileDrag={{
        boxShadow: "0 10px 30px -8px rgba(15, 23, 42, 0.25)",
        zIndex: 20,
      }}
      className={cn(
        "group relative transition-colors",
        dragging
          ? "bg-white dark:bg-slate-900"
          : "hover:bg-slate-50/50 dark:hover:bg-slate-800/30",
      )}
    >
      <TableCell className="w-10 pr-0">
        <button
          type="button"
          aria-label="Drag to reorder"
          onPointerDown={(e) => {
            e.preventDefault();
            controls.start(e);
          }}
          className="p-1 -m-1 rounded cursor-grab active:cursor-grabbing touch-none text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 transition-colors"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </TableCell>
      {children}
    </Reorder.Item>
  );
};

export const ComposedTable = <T,>({
  data,
  columns,
  searchQuery: externalSearch,
  searchPlaceholder = "Search…",
  statusOptions = ["All", "Active", "Inactive"],
  statusField,
  filters = [],
  filterFn,
  onAdd,
  emptyState,
  itemLabel = "items",
  pageSize = 10,
  onSearchChange,
  onFilterChange,
  onStatusChange,
  onReorder,
  getRowKey,
}: ComposedTableProps<T>) => {
  const [search, setSearch] = useState(externalSearch ?? "");
  const [statusFilter, setStatusFilter] = useState(statusOptions[0]);
  const [filterValues, setFilterValues] = useState<FilterValues>(() => {
    const initial: FilterValues = {};
    filters.forEach((f) => {
      if (f.defaultValue !== undefined) {
        initial[f.key] = f.defaultValue as any;
      }
    });
    return initial;
  });
  const [draftValues, setDraftValues] = useState<FilterValues>(filterValues);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Sync external search
  useEffect(() => {
    if (externalSearch !== undefined) setSearch(externalSearch);
  }, [externalSearch]);

  // Close popover on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Filter data client-side before handing to tanstack
  const filteredData = React.useMemo(() => {
    return data.filter((row) => {
      if (statusField && statusFilter !== statusOptions[0]) {
        const val =
          typeof statusField === "function"
            ? statusField(row)
            : String(row[statusField] ?? "");
        if (val.toLowerCase() !== statusFilter.toLowerCase()) return false;
      }
      if (filterFn && Object.keys(filterValues).length > 0) {
        if (!filterFn(row, filterValues)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, statusFilter, filterValues]);

  const { table } = useTable<T>({
    columns,
    data: filteredData,
    filter: search,
    pageSize,
  });

  const pageRows = table.getRowModel().rows;
  const totalFiltered = table.getFilteredRowModel().rows.length;
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const rowKeyOf = (row: { id: string; original: T }) =>
    getRowKey ? getRowKey(row.original) : row.id;
  const canReorder = !!onReorder && table.getState().sorting.length === 0;
  const pageKeys = pageRows.map(rowKeyOf);
  const pageKeysSignature = pageKeys.join("|");
  const [order, setOrder] = useState<string[]>(pageKeys);
  const orderRef = useRef(order);
  orderRef.current = order;
  useEffect(() => {
    setOrder(pageKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKeysSignature]);

  const commitReorder = () => {
    const current = orderRef.current;
    if (current.join("|") === pageKeysSignature) return;
    const byKey = new Map(pageRows.map((r) => [rowKeyOf(r), r.original]));
    onReorder?.(current.map((k) => byKey.get(k)).filter((r): r is T => r !== undefined));
  };

  const from = totalFiltered === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, totalFiltered);

  // Active filter count badge
  const activeFilterCount = Object.entries(filterValues).reduce(
    (acc, [, v]) => {
      if (Array.isArray(v)) return acc + v.length;
      if (v && v !== "All Time" && v !== "All") return acc + 1;
      return acc;
    },
    0,
  );

  const openFilterPopover = () => {
    setDraftValues({ ...filterValues });
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setFilterValues({ ...draftValues });
    if (onFilterChange) onFilterChange({ ...draftValues });
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    const empty: FilterValues = {};
    filters.forEach((f) => {
      if (f.defaultValue !== undefined) {
        empty[f.key] = f.defaultValue as any;
      } else {
        empty[f.key] =
          f.type === "chips" ? [] : f.type === "radio" ? f.options[0] : "";
      }
    });
    setDraftValues(empty);
    setFilterValues(empty);
    if (onFilterChange) onFilterChange(empty);
  };

  const removeFilter = (key: string, value?: string) => {
    setFilterValues((prev: any) => {
      let next;
      const current = prev[key];
      if (Array.isArray(current) && value) {
        next = { ...prev, [key]: current.filter((v: any) => v !== value) };
      } else {
        const def = filters.find((f) => f.key === key);
        const resetVal =
          def?.defaultValue !== undefined
            ? def.defaultValue
            : def?.type === "chips"
              ? []
              : def?.type === "radio"
                ? (def.options[0] ?? "")
                : "";
        next = { ...prev, [key]: resetVal };
      }
      if (onFilterChange) onFilterChange(next);
      return next;
    });
  };

  const clearAllFilters = () => {
    setSearch("");
    if (onSearchChange) onSearchChange("");
    setStatusFilter(statusOptions[0]);
    if (onStatusChange) onStatusChange(statusOptions[0]);
    resetFilters();
  };

  // Build active filter tags
  const activeTags: { key: string; label: string; value: string }[] = [];
  filters.forEach((f) => {
    const v = filterValues[f.key];
    if (!v) return;
    if (f.type === "toggle") {
      if (Boolean(v)) {
        activeTags.push({ key: f.key, label: f.label, value: "Enabled" });
      }
    } else if (Array.isArray(v)) {
      v.forEach((item) =>
        activeTags.push({ key: f.key, label: f.label, value: item }),
      );
    } else if (
      v &&
      v !== "All" &&
      v !== "All Time" &&
      v !== (f as Extract<FilterDef, { type: "radio" }>).options?.[0]
    ) {
      activeTags.push({ key: f.key, label: f.label, value: String(v) });
    }
  });
  const hasActiveFilters =
    search || statusFilter !== statusOptions[0] || activeTags.length > 0;

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900 min-h-[450px]">
      <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-900 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (onSearchChange) onSearchChange(e.target.value);
              table.setPageIndex(0);
            }}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[--primary]/20 focus:border-[--primary] transition-all"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Status tabs */}
          {statusOptions.length > 1 && (
            <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-xl gap-0.5">
              {statusOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => {
                    setStatusFilter(opt);
                    if (onStatusChange) onStatusChange(opt);
                    table.setPageIndex(0);
                  }}
                  className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 ${statusFilter === opt
                    ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                    }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}

          {/* More Filters */}
          {filters.length > 0 && (
            <div className="relative" ref={popoverRef}>
              <button
                onClick={() =>
                  isFilterOpen ? setIsFilterOpen(false) : openFilterPopover()
                }
                className={`flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${isFilterOpen ? "ring-2 ring-[--primary]" : ""}`}
              >
                <Filter className="w-4 h-4" />
                More Filters
                {activeFilterCount > 0 && (
                  <span className="flex items-center justify-center w-5 h-5 bg-[--primary] text-white text-[10px] rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {isFilterOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-4 z-50 animate-in fade-in zoom-in duration-200">
                  {filters.map((f) => (
                    <div key={f.key} className="mb-4 last:mb-0">
                      <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                        {f.label}
                      </h4>
                      {f.type === "text" && (
                        <input
                          type="text"
                          placeholder={f.placeholder ?? ""}
                          value={(draftValues[f.key] as string) ?? ""}
                          onChange={(e) =>
                            setDraftValues((prev) => ({
                              ...prev,
                              [f.key]: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-[12px] font-medium focus:ring-2 focus:ring-[--primary]/20 outline-none"
                        />
                      )}
                      {f.type === "chips" && (
                        <div className="flex flex-wrap gap-2">
                          {f.options.map((opt) => {
                            const selected = (
                              (draftValues[f.key] as string[]) ?? []
                            ).includes(opt);
                            return (
                              <button
                                key={opt}
                                onClick={() =>
                                  setDraftValues((prev) => {
                                    const cur = (prev[f.key] as string[]) ?? [];
                                    return {
                                      ...prev,
                                      [f.key]: selected
                                        ? cur.filter((v) => v !== opt)
                                        : [...cur, opt],
                                    };
                                  })
                                }
                                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all ${selected
                                  ? "bg-[--primary]/10 text-[--primary] border-[--primary]/30"
                                  : "bg-slate-50 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-slate-300"
                                  }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {f.type === "radio" && (
                        <div className="grid grid-cols-1 gap-2">
                          {(
                            f as Extract<FilterDef, { type: "radio" }>
                          ).options.map((opt) => {
                            const selected =
                              (draftValues[f.key] ??
                                (f as Extract<FilterDef, { type: "radio" }>)
                                  .options[0]) === opt;
                            return (
                              <button
                                key={opt}
                                onClick={() =>
                                  setDraftValues((prev) => ({
                                    ...prev,
                                    [f.key]: opt,
                                  }))
                                }
                                className={`text-left px-3 py-2 rounded-lg text-[12px] font-medium border transition-all ${selected
                                  ? "bg-[--primary]/5 text-[--primary] border-[--primary]/20"
                                  : "text-slate-600 dark:text-slate-400 border-transparent hover:bg-slate-50 dark:hover:bg-slate-800"
                                  }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {f.type === "toggle" && (
                        <div className="flex items-center justify-between py-1">
                          {f.description && (
                            <span className="text-[11px] text-slate-500 dark:text-slate-400">
                              {f.description}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setDraftValues((prev: any) => ({
                                ...prev,
                                [f.key]: !(prev[f.key] ?? f.defaultValue ?? false),
                              }))
                            }
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${(draftValues[f.key] ?? f.defaultValue ?? false)
                              ? "bg-[#0066b2]"
                              : "bg-slate-300 dark:bg-slate-700"
                              }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${(draftValues[f.key] ?? f.defaultValue ?? false)
                                ? "translate-x-4"
                                : "translate-x-0"
                                }`}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="pt-3 mt-2 border-t border-slate-100 dark:border-slate-800 flex justify-between">
                    <button
                      onClick={resetFilters}
                      className="text-[11px] font-bold text-slate-400 hover:text-red-500"
                    >
                      Reset
                    </button>
                    <button
                      onClick={applyFilters}
                      className="text-[11px] font-bold text-[--primary]"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Add button */}
          {onAdd && (
            <button
              onClick={onAdd}
              className="h-9 w-9 flex items-center justify-center rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors shrink-0"
            >
              <Plus
                className="w-4 h-4 text-slate-700 dark:text-slate-300"
                strokeWidth={2.5}
              />
            </button>
          )}
        </div>
      </div>

      {/* ── Active Filter Tags ── */}
      {hasActiveFilters && (
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-2 bg-slate-50/30 dark:bg-slate-800/20">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">
            Active Filters:
          </span>

          {search && (
            <FilterTag
              label={`Search: ${search}`}
              onRemove={() => {
                setSearch("");
                if (onSearchChange) onSearchChange("");
              }}
            />
          )}
          {statusFilter !== statusOptions[0] && (
            <FilterTag
              label={`Status: ${statusFilter}`}
              onRemove={() => {
                setStatusFilter(statusOptions[0]);
                if (onStatusChange) onStatusChange(statusOptions[0]);
              }}
            />
          )}
          {activeTags.map((tag) => (
            <FilterTag
              key={`${tag.key}-${tag.value}`}
              label={`${tag.label}: ${tag.value}`}
              onRemove={() => removeFilter(tag.key, tag.value)}
            />
          ))}
          <button
            onClick={clearAllFilters}
            className="text-[12px] font-bold text-slate-400 hover:text-red-500 transition-colors ml-1"
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="hover:bg-transparent">
                {canReorder && <TableHead className="w-10" />}
                {hg.headers.map((header) => (
                  <TableHeaderCell key={header.id} header={header} />
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {canReorder && pageRows.length > 0 ? (
            <Reorder.Group
              as="tbody"
              axis="y"
              values={order}
              onReorder={setOrder}
              className="divide-y divide-slate-100/60 dark:divide-slate-800/60"
            >
              {order.map((key) => {
                const row = pageRows.find((r) => rowKeyOf(r) === key);
                if (!row) return null;
                return (
                  <SortableRow key={key} rowKey={key} onDragEnd={commitReorder}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={
                          cell.column.columnDef.meta?.align === "right"
                            ? "text-right"
                            : ""
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </SortableRow>
                );
              })}
            </Reorder.Group>
          ) : (
          <TableBody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-20 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                      <Search className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      No results found
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Try adjusting your search or filters.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={
                        cell.column.columnDef.meta?.align === "right"
                          ? "text-right"
                          : ""
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
          )}
        </Table>
      </div>

      {/* ── Pagination ── */}
      {totalFiltered > 0 && (
        <div className="px-5 py-3.5 border-t border-slate-100 dark:border-slate-900 flex items-center justify-between text-xs text-slate-500 font-medium">
          <span>
            Showing {from} to {to} of {totalFiltered} {itemLabel}
          </span>
          {pageCount > 1 && (
            <div className="flex gap-2">
              <PaginationBtn
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft className="w-4 h-4" />
              </PaginationBtn>
              <PaginationBtn
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight className="w-4 h-4" />
              </PaginationBtn>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Internal helpers ─────────────────────────────────────────────────────────

const FilterTag: React.FC<{ label: string; onRemove: () => void }> = ({
  label,
  onRemove,
}) => (
  <span className="flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full text-[12px] font-medium text-slate-700 dark:text-slate-300 shadow-sm animate-in fade-in slide-in-from-left-2 duration-200">
    {label}
    <X
      className="w-3.5 h-3.5 cursor-pointer hover:text-red-500"
      onClick={onRemove}
    />
  </span>
);

const PaginationBtn: React.FC<{
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

// Augment ColumnMeta for align support
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData, TValue> {
    align?: "left" | "right" | "center";
  }
}
